import { computed } from 'vue';

import type { ConditionalRequestDefinition } from '@/utils/dehydrateRequestData/types';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';
import { ROLE, useClientStore } from '@/features/Clients';
import { FAHRERKREIS_TYP, KASKOTYP } from '@/features/Sparten/shared/types';
import { useInputStore } from '@/features/Sparten';
import {
  hasAnhaengerBauart,
  hasAssistenzsystem,
  hasAufbauart,
  hasFahrerkreis,
  hasFahrerschutzversicherung,
  hasJaehrlicheFahrleistung,
  hasNutzungsart,
  hasRabattschutzKfzHaftpflicht,
  hasRabattschutzKfzVollkasko,
  hasSfKlasseReactive,
} from '@/features/Sparten/shared/renderConditions/kfz/sharedRenderConditions';
import { kfzPreviousInsurance } from '@/features/Sparten/shared/utils/hasPreviousInsurance';

export const isNoAbweichenderHalter: ConditionalRequestDefinition = {
  if: computed(
    () => useInputStore().abweichenderHalter.isHalterAbweichend === false,
  ),
  drop: ['abweichenderHalter.typ'],
};

export const isErstbesitzer: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().fahrzeug.erstbesitzer),
  drop: ['fahrzeug.erstmaligeZulassungAufVN'],
};

export const isAlsBetriebsausgabeAnerkannt: ConditionalRequestDefinition = {
  if: computed(() =>
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.NUTZUNGSART,
      SIMPLE_CHOICE_META.NUTZUNGSART_PRIVAT,
    ).includes(useInputStore().fahrzeug.nutzungsart),
  ),
  drop: ['fahrzeug.isAlsBetriebsausgabeAnerkannt'],
};

export const isCompanyOrWorksInAutomotive: ConditionalRequestDefinition = {
  if: computed(
    () =>
      useInputStore().versicherungsnehmer.isFirma === true ||
      useInputStore().versicherungsnehmer.branche !== 'Automobilbranche',
  ),
  drop: ['versicherungsnehmer.isMitarbeiterAutohersteller'],
};

export const isNotGermanOrIsCompany: ConditionalRequestDefinition = {
  if: computed(() => {
    const clientStore = useClientStore();

    return (
      useInputStore().versicherungsnehmer.isFirma === true ||
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.NATIONALITY,
        SIMPLE_CHOICE_META.NATIONALITY_GERMAN,
      ).includes(clientStore[ROLE.VERSICHERUNGSNEHMER].data.nationalitaet)
    );
  }),
  drop: ['versicherungsnehmer.hasWohnsitzMin5JahreInBrd'],
};

export const isFahrerkreisVNDriver: ConditionalRequestDefinition = {
  if: computed(
    () => useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.NUR_VN,
  ),
  drop: [
    'fahrerkreis.isVnFahrer',
    'fahrerkreis.isVnHauptnutzer',
    'fahrerkreis.altersbeschraenkung',
    'fahrerkreis.alterJuengsterFahrer',
    'fahrerkreis.alterAeltesterFahrer',
  ],
};

export const isFahrerkreisFix: ConditionalRequestDefinition = {
  if: computed(
    () => useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.FESTGELEGT,
  ),
  drop: ['fahrerkreis.altersbeschraenkung'],
};

export const isFahrerkreisAny: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.OFFEN),
  drop: ['fahrerkreis.isVnHauptnutzer'],
};

export const isFahrerkreisNoAgeRestriction: ConditionalRequestDefinition = {
  if: computed(
    () =>
      useInputStore().fahrerkreis.typ !== FAHRERKREIS_TYP.OFFEN ||
      useInputStore().fahrerkreis.altersbeschraenkung === false,
  ),
  drop: [
    'fahrerkreis.alterJuengsterFahrer',
    'fahrerkreis.alterAeltesterFahrer',
  ],
};

export const isNoSaisonkennzeichen: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().isSaisonkennzeichen === false),
  drop: ['saisonkennzeichen.monatsAnfang', 'saisonkennzeichen.monatsEnde'],
};

export const isNotAnAdditionalCar: ConditionalRequestDefinition = {
  if: computed(
    () =>
      !getSimpleChoiceMeta(
        SIMPLE_CHOICE.VERSICHERUNGSART_NEUES_FAHRZEUG,
        SIMPLE_CHOICE_META.VERSICHERUNGSART_NEUES_FAHRZEUG_IS_ZWEITWAGEN,
      ).includes(useInputStore().allgemein.versicherungsart),
  ),
  drop: ['erstfahrzeug'],
};

export const isNoVollkasko: ConditionalRequestDefinition = {
  if: computed(
    () =>
      !getSimpleChoiceMeta(
        SIMPLE_CHOICE.KASKO_TYP,
        SIMPLE_CHOICE_META.KASKO_TYP_VOLLKASKO,
      ).includes(useInputStore().kaskoTyp),
  ),
  drop: ['kaskoSB.gleicheSB', 'kaskoSB.vollkasko', 'sfKlasseVK'],
};

export const isNoTeilkasko: ConditionalRequestDefinition = {
  if: computed(
    () =>
      !getSimpleChoiceMeta(
        SIMPLE_CHOICE.KASKO_TYP,
        SIMPLE_CHOICE_META.KASKO_TYP_TEILKASKO,
      ).includes(useInputStore().kaskoTyp) &&
      (!getSimpleChoiceMeta(
        SIMPLE_CHOICE.KASKO_TYP,
        SIMPLE_CHOICE_META.KASKO_TYP_VOLLKASKO,
      ).includes(useInputStore().kaskoTyp) ||
        useInputStore().kaskoSB.gleicheSB),
  ),
  drop: ['kaskoSB.teilkasko'],
};

// @todo create choice meta to use getSimpleChoiceMeta here
export const isHaftpflicht: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().kaskoTyp === 'haftpflicht'),
  drop: ['kaskoSB'],
};

export const isNoSaisonKennzeichen: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().isSaisonkennzeichen === false),
  drop: ['saisonkennzeichen'],
};

export const isNoJahreskarteOffentlicherPersonenverkehr: ConditionalRequestDefinition =
  {
    if: computed(
      () =>
        useInputStore().versicherungsnehmer
          .hasJahreskarteOeffentlicherPersonenverkehr === false,
    ),
    drop: ['versicherungsnehmer.jahreskarteOeffentlicherPersonenverkehr'],
  };

export const isNoWohnungseigentum: ConditionalRequestDefinition = {
  if: computed(
    () => useInputStore().versicherungsnehmer.hasWohneigentum === false,
  ),
  drop: ['versicherungsnehmer.wohneigentum'],
};

export const isNoMinors: ConditionalRequestDefinition = {
  if: computed(() =>
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.MINOR_CHILDREN,
      SIMPLE_CHOICE_META.MINOR_CHILDREN_KEIN_KIND,
    ).includes(useInputStore().versicherungsnehmer.hasMinderjaehrigeKinder),
  ),
  drop: ['versicherungsnehmer.minderjaehrigeKinder'],
};

export const hasOneChild: ConditionalRequestDefinition = {
  if: computed(() =>
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.MINOR_CHILDREN,
      SIMPLE_CHOICE_META.MINOR_CHILDREN_EIN_KIND,
    ).includes(useInputStore().versicherungsnehmer.hasMinderjaehrigeKinder),
  ),
  drop: [
    'versicherungsnehmer.minderjaehrigeKinder.alterAeltestesKind',
    'versicherungsnehmer.minderjaehrigeKinder.alterJuengstesKind',
  ],
};

export const hasSeveralChildren: ConditionalRequestDefinition = {
  if: computed(() =>
    getSimpleChoiceMeta(
      SIMPLE_CHOICE.MINOR_CHILDREN,
      SIMPLE_CHOICE_META.MINOR_CHILDREN_HAS_MEHRERE_KINDER,
    ).includes(useInputStore().versicherungsnehmer.hasMinderjaehrigeKinder),
  ),
  drop: ['versicherungsnehmer.minderjaehrigeKinder.alter'],
};

export const isNoVorversicherung: ConditionalRequestDefinition = {
  if: computed(() => !kfzPreviousInsurance()),
  drop: ['vorversicherung'],
};

export const hasNoDamage: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().vorschaeden?.length === 0),
  drop: ['vorschaeden'],
};

export const isOnlyHaftpflichtFeature: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().kaskoTyp === KASKOTYP.VOLLKASKO),
  drop: ['rabattschutzKfzHaftpflicht'],
};

export const isNotHaftpflichtFeature: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().kaskoTyp === KASKOTYP.HAFTPFLICHT),
  drop: ['kollisionMitTieren', 'werkstattbindung', 'tierbissfolgeschaeden'],
};

export const hasGapDeckung: ConditionalRequestDefinition = {
  if: computed(
    () =>
      useInputStore().kaskoTyp === KASKOTYP.HAFTPFLICHT ||
      !getSimpleChoiceMeta(
        SIMPLE_CHOICE.FINANZIERUNGSART,
        SIMPLE_CHOICE_META.FINANZIERUNGSART_NOT_SELF_FINANCING,
      ).includes(useInputStore().fahrzeug.finanzierungsart),
  ),
  drop: ['gapDeckung'],
};

export const isOnlyVollkaskoFeature: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().kaskoTyp !== KASKOTYP.VOLLKASKO),
  drop: ['rabattschutzVollkasko'],
};

export const isVnVorsteuerabzugsberechtigt: ConditionalRequestDefinition = {
  if: computed(
    () =>
      !getSimpleChoiceMeta(
        SIMPLE_CHOICE.KFZ_BERUFSSTATUS,
        SIMPLE_CHOICE_META.KFZ_BERUFSSTATUS_IS_VORSTEUER_ABZUGS_BERECHTIGT,
      ).includes(useInputStore().versicherungsnehmer.berufsstatus),
  ),
  drop: ['versicherungsnehmer.isVorsteuerAbzugsberechtigt'],
};

export const isAnhaengerBauartAvailable: ConditionalRequestDefinition = {
  if: computed(() => !hasAnhaengerBauart.value),
  drop: [
    'fahrzeug.anhaengerVerleihung',
    'fahrzeug.gesellschaftZugfahrzeug',
    'fahrzeug.anhaengerBauart',
  ],
};

export const isAufbauartAvailable: ConditionalRequestDefinition = {
  if: computed(() => !hasAufbauart.value),
  drop: ['fahrzeug.aufbauart'],
};

export const isJaehrlicheFahrleistungAvailable: ConditionalRequestDefinition = {
  if: computed(() => !hasJaehrlicheFahrleistung.value),
  drop: ['fahrzeug.fahrleistung'],
};

export const isNutzungsartAvailable: ConditionalRequestDefinition = {
  if: computed(() => !hasNutzungsart.value),
  drop: ['fahrzeug.nutzungsart'],
};

export const isFahrerkreisAvailable: ConditionalRequestDefinition = {
  if: computed(() => !hasFahrerkreis.value),
  drop: ['fahrerkreis'],
};

export const isSfKlasseAvailable: ConditionalRequestDefinition = {
  if: computed(() => !hasSfKlasseReactive.value),
  drop: ['sfKlasseKH', 'sfKlasseVK'],
};

export const isFahrerschutzversicherungAvailable: ConditionalRequestDefinition =
  {
    if: computed(() => !hasFahrerschutzversicherung.value),
    drop: ['fahrerschutzversicherung'],
  };

export const isRabattschutzKfzHaftpflichtAvailable: ConditionalRequestDefinition =
  {
    if: computed(() => !hasRabattschutzKfzHaftpflicht.value),
    drop: ['rabattschutzKfzHaftpflicht'],
  };

export const isRabattschutzKfzVollkaskoAvailable: ConditionalRequestDefinition =
  {
    if: computed(() => !hasRabattschutzKfzVollkasko.value),
    drop: ['rabattschutzVollkasko'],
  };

export const isAssistenzsystemAvailable: ConditionalRequestDefinition = {
  if: computed(() => !hasAssistenzsystem.value),
  drop: ['fahrzeug.assistenzsysteme'],
};

export const temporaryField: ConditionalRequestDefinition = {
  if: computed(() => true),
  drop: ['versicherungsnehmer.isFullAge'],
};
